import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type NavigationMode = 'left' | 'top';

@Injectable({ providedIn: 'root' })
export class LayoutPreferencesService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = 'bilateral.layout.preferences';
  private readonly browser = isPlatformBrowser(this.platformId);

  readonly navigationMode = signal<NavigationMode>('left');
  readonly sidebarCollapsed = signal(false);
  readonly topNavigationVisible = signal(true);
  readonly topDrawerOpen = signal(false);
  readonly topDrawerCompact = signal(false);
  readonly canCollapseSidebar = computed(() => this.navigationMode() === 'left');

  constructor() {
    if (!this.browser) return;

    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw) as Partial<{
        navigationMode: NavigationMode;
        sidebarCollapsed: boolean;
        topNavigationVisible: boolean;
        topDrawerCompact: boolean;
      }>;
      if (parsed.navigationMode === 'left' || parsed.navigationMode === 'top') {
        this.navigationMode.set(parsed.navigationMode);
      }
      if (typeof parsed.sidebarCollapsed === 'boolean') {
        this.sidebarCollapsed.set(parsed.sidebarCollapsed);
      }
      if (typeof parsed.topNavigationVisible === 'boolean') {
        this.topNavigationVisible.set(parsed.topNavigationVisible);
      }
      if (typeof parsed.topDrawerCompact === 'boolean') {
        this.topDrawerCompact.set(parsed.topDrawerCompact);
      }
    } catch {
      window.localStorage.removeItem(this.storageKey);
    }
  }

  toggleNavigationMode(): void {
    this.navigationMode.update((mode) => (mode === 'left' ? 'top' : 'left'));
    this.topNavigationVisible.set(true);
    this.topDrawerOpen.set(false);
    this.persist();
  }

  toggleSidebarCollapsed(): void {
    if (this.navigationMode() !== 'left') return;
    this.sidebarCollapsed.update((value) => !value);
    this.persist();
  }

  toggleTopNavigationVisible(): void {
    if (this.navigationMode() !== 'top') return;
    this.topNavigationVisible.update((value) => !value);
    this.persist();
  }

  toggleTopDrawer(): void {
    if (this.navigationMode() !== 'top') return;
    this.topDrawerOpen.update((value) => !value);
  }

  toggleTopDrawerCompact(): void {
    if (this.navigationMode() !== 'top') return;
    this.topDrawerCompact.update((value) => !value);
    this.persist();
  }

  closeTopDrawer(): void {
    this.topDrawerOpen.set(false);
  }

  private persist(): void {
    if (!this.browser) return;

    window.localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        navigationMode: this.navigationMode(),
        sidebarCollapsed: this.sidebarCollapsed(),
        topNavigationVisible: this.topNavigationVisible(),
        topDrawerCompact: this.topDrawerCompact(),
      }),
    );
  }
}
